import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { xlsxToWorkbook, workbookToJSON } from "components/Shared/Formatters/XLSX";
import { Card, Button } from "react-md";
import { userHasRight } from "selectors/rights";
import { FIELD_SERVICE_AUTO_QA_BATCH_ADD } from "typeConstants/rights";
import { runBulkAutoQAProcess } from "requests/autoQA";
import { MSpinner, MDropzone } from "components/Shared";
import responseHandler from "responseHandler";
const { EXCEL } = MDropzone.fileTypes;

const BulkAutoQA = props => {
  const [{ clear, files, errors }, setDropzoneFunctions] = useState({ clear: () => { }, files: [], errors: [] });
  const [controlNumbersForUpload, setControlNumbersForUpload] = useState([]);
  const [sendingBatch, setSendingBatch] = useState(false);
  const mounted = useRef(true);

  useEffect(() => {
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    const parseWorkbook = async () => {
      const workbook = await xlsxToWorkbook(files[0].file);
      const jsonData = workbookToJSON(workbook);
      let controlNumbers = [];
      if (jsonData?.length) {
        // This is an array of arrays
        jsonData[0].forEach(array => {
          // Ignore header
          if (!isNaN(parseInt(array[0]))) {
            // Take control numbers from the first column
            controlNumbers.push(parseInt(array[0]));
          }
        });
      } 
      if (controlNumbers.length > 0) {
        setControlNumbersForUpload(controlNumbers);
      } else {
        props.showErrorToast("No control numbers found. Please check spreadsheet. Control numbers should be in the first column in the first sheet.");
      }
    };
    if (files[0]) {
      parseWorkbook();
    }
  }, [files.length]);

  useEffect(() => {
    if (errors.length) {
      for (let error of errors) {
        props.showErrorToast(error);
      }
    }
  }, [errors]);

  const reset = () => {
    clear();
    setControlNumbersForUpload([]);
  };

  const onSubmit = () => {
    setSendingBatch(true);
    runBulkAutoQAProcess(controlNumbersForUpload, submitCallback, mounted);
  };

  const submitCallback = () => {
    setSendingBatch(false);
    props.showSuccessToast(`${controlNumbersForUpload.length} Control numbers uploaded for Auto QA`);
    setControlNumbersForUpload([]);
  };

  const onFileRemoved = () => {
    setControlNumbersForUpload([]);
  };

  return (
    <div className="padding-page-content full-height">
      <Card className="bulk-auto-qa-container">
        {sendingBatch 
          ? <MSpinner /> 
          : <>
              <p>Instructions</p>
              <p>Upload a spreadsheet with control numbers in the first column of the first sheet. Headers will be ignored.</p>
              <p>Cases must be in QA status to be eligible for Auto-QA.</p>
              <div className="bulk-auto-qa-dropzone-container">
                <MDropzone
                  className={"bulk-auto-qa-dropzone"}
                  setDropzoneProperties={setDropzoneFunctions}
                  onFileAdd={() => { }}
                  onFileRemove={onFileRemoved}
                  prompt="Click here to choose a file to upload"
                  validFileTypes={{
                    type: EXCEL,
                  }}
                  trackFiles
                />
              </div>
              {controlNumbersForUpload?.length > 0 && <p>{`${controlNumbersForUpload.length} Control numbers found in spreadsheet`}</p>}
              <div className="bulk-auto-qa-buttons">
                <Button
                  primary
                  raised
                  onClick={onSubmit}
                  disabled={!props.canSubmitAutoQABatch || controlNumbersForUpload.length === 0}
                >
                  Upload Batch
                </Button>
                <Button
                  primary
                  raised
                  onClick={reset}
                  disabled={files.length === 0}
                >
                  Remove Spreadsheet
                </Button>
              </div>
            </>}
      </Card>
    </div>
  );
};

BulkAutoQA.propTypes = {
  canSubmitAutoQABatch: PropTypes.bool,
  showErrorToast: PropTypes.func,
  showSuccessToast: PropTypes.func,
};

const mapStateToProps = (state) => ({
  canSubmitAutoQABatch: userHasRight(FIELD_SERVICE_AUTO_QA_BATCH_ADD)(state),
});

const mapDispatchToProps = (dispatch) => ({
  showErrorToast(text) {
    responseHandler.customToast(text, "error", dispatch);
  },
  showSuccessToast(text) {
    responseHandler.customToast(text, "success", dispatch);
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(BulkAutoQA);